import type { AttributeType } from 'cadenza/repository/model/attribute-type.gen';
import { getTargetAttributeType } from 'cadenza/repository/model/measure-aggregation.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { getViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { getAllAttributeIdsFromDataView } from 'cadenza/workbook/data-view/data-view-utils';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getAcceptedAttributeIdsByDataViewId } from 'cadenza/workbook/state/workbook-reachability.state';
import { getAllowedAggregations, isLobAttributeWithoutRelation } from 'cadenza/workbook/workbook-attribute-util';
import {
  getAttributeById,
  getDataViewById,
  getObjecttypeById,
  getViewByDataViewId,
  hasObjecttypeDatasourceCapabilities,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { AttributeBundleValidatorMessages } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator-messages';
import { DEFAULT_MESSAGES } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator-messages';
import i18n from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator-messages.properties';
import type { FilterMessages } from 'cadenza/workbook/workbook-panel/settings/attributes/drop-controller';
import { validateBundleAttributeRelationCardinality } from 'cadenza/workbook/workbook-panel/settings/attributes/validate-bundle-attributes-cardinality';
import { CustomAggregationAttributeBundleValidator } from 'cadenza/workbook/workbook-panel/settings/attributes/validators/custom-aggregation-attribute-bundle-validator';
import { WritebackTableAttributeBundleValidator } from 'cadenza/workbook/workbook-panel/settings/attributes/validators/writeback-table-attribute-bundle-validator/writeback-table-attribute-bundle-validator';

export interface IAttributeBundleValidator {
  validate: (
    bundleDefinition: AttributeBundleDefinitionDto,
    attribute: WorkbookAttributeDto,
  ) => AttributeBundleValidationResult | undefined;
}

export interface AttributeBundleValidationResult {
  accepts: boolean;
  message?: string;
}

function hasRequiredDataSourceCapabilitiesForBundle(
  bundleDefinition: AttributeBundleDefinitionDto,
  attribute: WorkbookAttributeDto,
) {
  const requiredDataSourceCapabilities = bundleDefinition.requiredDataSourceCapabilities;
  return (
    requiredDataSourceCapabilities.length === 0 ||
    hasObjecttypeDatasourceCapabilities(getStore().getState(), attribute.objecttypeId, requiredDataSourceCapabilities)
  );
}

interface AttributeBundleValidatorOptions {
  dataViewId?: DataViewId;
  attributesProvider: (dataViewId: DataViewId) => WorkbookAttributeDto[];
  filterMessages?: AttributeBundleValidatorMessages;
  acceptedObjecttypeRolesErrorMessage?: string;
  enforceAttributesNumberLimit?: boolean;
  requiresRelationForLobAttribute?: boolean;
}

export class AttributeBundleValidator {
  readonly #dataViewId: DataViewId;
  readonly #attributesProvider: (dataViewId: DataViewId) => WorkbookAttributeDto[];
  readonly #filterMessages: FilterMessages & AttributeBundleValidatorMessages;
  readonly #acceptedObjecttypeRolesErrorMessage?: string;
  readonly #validators: IAttributeBundleValidator[] = [];

  constructor(options: AttributeBundleValidatorOptions) {
    this.#filterMessages = {
      ...DEFAULT_MESSAGES,
      ...(options.filterMessages ?? {}),
    };
    this.#dataViewId = options.dataViewId!;
    this.#attributesProvider = options.attributesProvider;
    this.#acceptedObjecttypeRolesErrorMessage = options.acceptedObjecttypeRolesErrorMessage;

    this.#validators.push(
      new CustomAggregationAttributeBundleValidator(),
      new WritebackTableAttributeBundleValidator(this.#dataViewId),
    );
  }

  dropControllerAccepts(
    bundleDefinition: AttributeBundleDefinitionDto,
    attribute: WorkbookAttributeDto,
    requiresRelationForLobAttribute = true,
    enforceAttributesNumberLimit = true,
    targetAttributeTypeHasPrecedence = true,
  ): AttributeBundleValidationResult {
    if (!attribute) {
      // in case of virtual attribute we get null and undefined in case attribute is not present
      return { accepts: false, message: this.#filterMessages.virtualAttributeErrorMessage };
    }

    if (requiresRelationForLobAttribute && isLobAttributeWithoutRelation(attribute)) {
      return { accepts: false, message: this.#filterMessages.lobAttributeWithoutRelationErrorMessage };
    }

    if (enforceAttributesNumberLimit && !this.#areLimitsRespected(attribute, bundleDefinition)) {
      return { accepts: false, message: this.#filterMessages.limitsReachedErrorMessage };
    }

    const acceptedAttributeIds = this.#getAcceptedAttributeIds();
    if (
      acceptedAttributeIds.length && // Empty means all accepted
      !acceptedAttributeIds.includes(attribute.baseId ?? attribute.id)
    ) {
      return { accepts: false, message: this.#filterMessages.acceptedObjecttypeIdsErrorMessage };
    }

    if (
      bundleDefinition.acceptedAttributeCategories.length && // Empty means all accepted
      !bundleDefinition.acceptedAttributeCategories.includes(attribute.category)
    ) {
      return { accepts: false, message: this.#filterMessages.acceptedCategoriesErrorMessage };
    }

    const bundleDontAcceptsRelatedGeometryTypes =
      bundleDefinition.acceptedGeometryTypes.length &&
      !bundleDefinition.acceptedGeometryTypes.includes(attribute.relatedGeometryType!);

    if (bundleDontAcceptsRelatedGeometryTypes) {
      return { accepts: false, message: this.#filterMessages.acceptedGeometryTypesErrorMessage };
    }

    // Check `status`, as calculated attributes that are broken due to referencing an attribute with an `UNKNOWN`
    // attribute type still retain their originally configured attribute type in `type`.
    if (attribute.status === 'UNKNOWN_TYPE') {
      return { accepts: false, message: this.#filterMessages.unknownAttributeTypeErrorMessage };
    }

    const acceptedTypes = bundleDefinition.acceptedAttributeTypes[attribute.category];
    if (
      attribute.type &&
      !bundleDefinition.acceptedGeometryTypes.length && // geometry types can still be valid
      !isAcceptedAttributeType(attribute, acceptedTypes, targetAttributeTypeHasPrecedence)
    ) {
      return { accepts: false, message: this.#filterMessages.acceptedAttributeTypesErrorMessage };
    }

    for (let validator of this.#validators) {
      const validationResult = validator.validate(bundleDefinition, attribute);
      if (validationResult) {
        return validationResult;
      }
    }

    return { accepts: true };
  }

  dataViewControllerAccepts(
    bundleDefinition: AttributeBundleDefinitionDto,
    attribute: WorkbookAttributeDto,
    requiresRelationForLobAttribute = true,
    enforceAttributesNumberLimit = true,
    targetAttributeTypeHasPrecedence = true,
  ): AttributeBundleValidationResult {
    const result = this.dropControllerAccepts(
      bundleDefinition,
      attribute,
      requiresRelationForLobAttribute,
      enforceAttributesNumberLimit,
      targetAttributeTypeHasPrecedence,
    );
    if (!result.accepts) {
      return result;
    }
    if (this.#isHierarchyAlreadyAdded(attribute)) {
      return { accepts: false, message: this.#filterMessages.hierarchyAlreadyAddedMessage };
    }
    if (bundleDefinition.acceptedObjecttypeRoles?.length) {
      const objecttype = getObjecttypeById(getStore().getState(), attribute.objecttypeId);
      const roleCategory = objecttype.properties!['self-service-content-role-category'];
      const role = objecttype.properties!['self-service-content-role'];
      const roleString = `${roleCategory}/${role}`;
      if (!roleCategory || !role || !bundleDefinition.acceptedObjecttypeRoles.includes(roleString)) {
        return { accepts: false, message: this.#acceptedObjecttypeRolesErrorMessage };
      }
    }

    // check if the attribute's datasource has the required capabilities for this bundle
    if (!hasRequiredDataSourceCapabilitiesForBundle(bundleDefinition, attribute)) {
      return { accepts: false, message: i18n('missing-database-capability') };
    }
    // For maps, check the cardinality between the geometry bundle and other restricted bundles
    if (this.#isMapView()) {
      if (!validateBundleAttributeRelationCardinality(this.#dataViewId, bundleDefinition, attribute)) {
        return { accepts: false, message: i18n('geometry-to-other-bundle-cardinality') };
      }
    }
    // For charts, check the cardinality between the last category attribute and other coloring attribute bundle
    if (this.#isChartViewSupportingColoringBundle()) {
      if (!validateBundleAttributeRelationCardinality(this.#dataViewId, bundleDefinition, attribute)) {
        return { accepts: false, message: i18n('chart-coloring-bundle-cardinality') };
      }
    }

    return { accepts: true };
  }

  #isMapView() {
    return getViewByDataViewId(getStore().getState(), this.#dataViewId).type === 'MAP';
  }

  #isChartViewSupportingColoringBundle() {
    const dataView = getDataViewById(getStore().getState(), this.#dataViewId);
    const isChart = getViewType(dataView.visualizationKey) === 'CHART';
    const hasColorClassificationBundle = dataView.attributeBundleDefinitions['COLOR_CLASSIFICATION'] != null;
    return isChart && hasColorClassificationBundle;
  }

  #areLimitsRespected(attribute: WorkbookAttributeDto, bundleDefinition: AttributeBundleDefinitionDto) {
    const attributes = [...this.#attributesProvider(this.#dataViewId), attribute];
    const maxAttributes = bundleDefinition.maxNumberOfAttributes ?? Infinity;
    const maxLevels = bundleDefinition.maxNumberOfLevels ?? Infinity;

    return attributes.length <= maxAttributes && getNumberOfLevels(attributes) <= maxLevels;
  }

  #getAcceptedAttributeIds() {
    return this.#dataViewId ? getAcceptedAttributeIdsByDataViewId(getStore().getState(), this.#dataViewId) : [];
  }

  /**
   * Checks whether the given attribute is a hierarchy and already is added on any bundle of the
   * data view.
   *
   * @protected
   * @param {import('cadenza/workbook/api').AttributeDto} attribute - The attribute to check
   * @return {boolean} True if hierarchy and already added to the data view, else false
   */
  #isHierarchyAlreadyAdded(attribute: WorkbookAttributeDto) {
    if (attribute.hierarchyType) {
      const baseIdToAdd = attribute.id;
      const dataView = getDataViewById(getStore().getState(), this.#dataViewId);
      const allAttributeIdsFromDataView = getAllAttributeIdsFromDataView(dataView);
      return allAttributeIdsFromDataView.some((attributeId) => getAttributeBaseId(attributeId) === baseIdToAdd);
    }
    return false;
  }
}

function isAcceptedAttributeType(
  attribute: WorkbookAttributeDto,
  acceptedTypes: AttributeType[] | undefined,
  targetAttributeTypeHasPrecedence: boolean,
) {
  // Empty means all accepted
  if (!acceptedTypes?.length || acceptedTypes.includes(attribute.type)) {
    return true;
  }
  if (attribute.category !== 'MEASURE' || attribute.measureAggregation === 'CUSTOM') {
    return false;
  }

  if (!targetAttributeTypeHasPrecedence) {
    return acceptedTypes.includes(attribute.type);
  }
  return getAllowedAggregations(attribute).some((aggregation) =>
    acceptedTypes.includes(getTargetAttributeType(attribute.type, aggregation)),
  );
}

function getAttributeBaseId(activeAttributeId: ActiveAttributeId) {
  return getAttributeById(getStore().getState(), activeAttributeId).baseId;
}

function getNumberOfLevels(attributes: WorkbookAttributeDto[]) {
  // Only AttributeCategory.HIERARCHY has set the 'level' property and thus can have > 1 levels
  return attributes.reduce((sum, { levels }) => sum + (levels?.length ?? 1), 0);
}
